import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AuthHealthMonitor — global watchdog against genuinely stuck loading states.
 *
 * Mounted once at the App root next to <Toaster />. Doesn't render
 * anything; purely an effect.
 *
 * ── Timing invariant (pinned by tests/test_auth_watchdog_invariant.py) ──
 *
 *   /auth/me first attempt   ≤ 30_000 ms   (api.js AUTH_ME_TIMEOUT_MS)
 *   retry backoff            +  1_500 ms   (api.js AUTH_ME_RETRY_BACKOFF_MS)
 *   /auth/me retry attempt   ≤ 15_000 ms   (api.js AUTH_ME_RETRY_TIMEOUT_MS)
 *   ────────────────────────────────────────
 *   TOTAL AUTH BUDGET          46_500 ms
 *
 *   WATCHDOG_MS = 60_000 ms  ← must be strictly greater than the total
 *                              above so a slow-but-working /auth/me can
 *                              never trip this watchdog. 13.5s margin.
 *
 * ── Behavior (Jul 3 2026 rewrite) ─────────────────────────────────────
 *
 * Prior version force-navigated to `/login?expired=1` after 15s of loading,
 * which produced false "Session expired" logouts on Indian mobile data
 * (median /auth/me round-trip 8-14s under packet loss). That behavior is
 * REMOVED. The watchdog now:
 *
 *   1. At 60s of `loading = true`, calls `refresh()` from the auth context
 *      to attempt a fresh re-hydration with the user's existing token.
 *      This is a re-attempt, not a logout. Token is preserved.
 *
 *   2. If loading STILL true 60s after that (i.e. 120s total), we assume
 *      the JS state itself is stuck (unhandled error, service worker
 *      quirk, WatchFiles reload glitch, etc.) and do a hard page reload.
 *      Reload preserves the token in localStorage and the current URL, so
 *      the user resumes exactly where they were once boot completes.
 *
 * The watchdog NEVER wipes the token and NEVER redirects to /login. Only
 * a real 401/403 from the backend can end a session (handled cleanly in
 * AuthContext.refresh()).
 *
 * We defer to AuthCallback's own 25s watchdog for legitimate OAuth callback
 * flows — it has a richer error UI for in-flight OAuth POSTs.
 */

// EXPORTED so test_auth_watchdog_invariant.py can parse and pin the value.
export const WATCHDOG_MS = 60000;
// If loading remains true this long AFTER the first watchdog action,
// assume the app is truly stuck and hard-reload.
export const WATCHDOG_RELOAD_MS = 60000;

const AuthHealthMonitor = () => {
  const { loading, refresh } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // If we're not in a loading state, nothing to monitor.
    if (!loading) return;

    // If we ARE in a legitimate OAuth callback flow, defer to
    // AuthCallback's own 25s watchdog — it has a proper error UI.
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    if (hash.includes("session_id=") || search.includes("session_id=")) return;

    let reloadTimer = null;

    const firstTimer = setTimeout(() => {
      const diag = {
        ts: new Date().toISOString(),
        loading: true,
        pathname: window.location.pathname,
        hash: window.location.hash,
        search: window.location.search,
        hasStash: false,
        userAgent: navigator.userAgent.slice(0, 200),
      };
      try { diag.hasStash = !!sessionStorage.getItem("__bhrigu_oauth_sid"); } catch (e) { /**/ }
      try { sessionStorage.removeItem("__bhrigu_oauth_sid"); } catch (e) { /**/ }
      // Single structured log so it shows up cleanly in console + PostHog.
      console.warn("[AuthHealthMonitor] loading > 60s — re-hydrating existing session (NOT logging out)", diag);

      // Re-hydration attempt — call refresh() with the token still in
      // localStorage. This is the honest recovery: if the network was just
      // slow, this retry usually resolves and the user proceeds silently.
      try {
        if (typeof refresh === "function") {
          refresh();
        }
      } catch (e) {
        console.warn("[AuthHealthMonitor] refresh() threw", e);
      }

      // Second-tier safety net: if the app is genuinely stuck (JS error,
      // dead promise, browser extension intercept), reload the page after
      // another WATCHDOG_RELOAD_MS. Reload preserves token + URL and gives
      // the app a clean boot rather than a misleading login redirect.
      reloadTimer = setTimeout(() => {
        console.error("[AuthHealthMonitor] loading still stuck after re-hydration — hard reloading", {
          ts: new Date().toISOString(),
          pathname: window.location.pathname,
        });
        window.location.reload();
      }, WATCHDOG_RELOAD_MS);
    }, WATCHDOG_MS);

    return () => {
      clearTimeout(firstTimer);
      if (reloadTimer) clearTimeout(reloadTimer);
    };
  // We deliberately include `location` so a route change resets the timer.
  // Eg user navigates from /app → /faq while loading=true; new timer starts.
  }, [loading, location, refresh]);

  return null;
};

export default AuthHealthMonitor;
